
import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { 
  FileDown, 
  Clock, 
  Users, 
  TrendingUp, 
  TrendingDown, 
  Award, 
  Calendar, 
  Search 
} from "lucide-react";

// Sample data for the dashboard
const dashboardData = {
  activeMembers: 8420,
  pointsEarned: 620000,
  pointsRedeemed: 478000,
  redemptionRate: 77,
  avgPointsPerTransaction: 72
};

// Sample data for member insights
const sampleMemberData = [
  {
    id: "M12345",
    name: "Ahmad Bin Abdullah",
    phone: "012-345-6789",
    tier: "Gold",
    totalPoints: 15230,
    lastActivity: "2025-05-10",
    totalSpent: 12450.50,
    redemptions: 32
  },
  {
    id: "M67890",
    name: "Siti Aminah",
    phone: "012-987-6543",
    tier: "Silver",
    totalPoints: 3820,
    lastActivity: "2025-05-15",
    totalSpent: 5340.75,
    redemptions: 12
  },
  {
    id: "M23456",
    name: "Raj Kumar",
    phone: "016-789-0123",
    tier: "Gold",
    totalPoints: 9750,
    lastActivity: "2025-05-12",
    totalSpent: 8920.25,
    redemptions: 18
  },
  {
    id: "M78901",
    name: "Nurul Huda",
    phone: "013-456-7890",
    tier: "Silver",
    totalPoints: 2340,
    lastActivity: "2025-05-08",
    totalSpent: 3120.80,
    redemptions: 5
  },
  {
    id: "M34567",
    name: "Chong Wei Ming",
    phone: "019-876-5432",
    tier: "Basic",
    totalPoints: 760,
    lastActivity: "2025-04-20",
    totalSpent: 1250.35,
    redemptions: 2
  }
];

// Sample data for inactive members
const sampleInactiveMembersData = [
  {
    id: "M45678",
    name: "Lee Mei Ling",
    phone: "017-654-3210",
    tier: "Silver",
    lastActivity: "2025-01-15",
    daysInactive: 125,
    totalPoints: 2430
  },
  {
    id: "M89012",
    name: "Tan Ah Kow",
    phone: "014-321-0987",
    tier: "Basic",
    lastActivity: "2025-01-30",
    daysInactive: 110,
    totalPoints: 320
  },
  {
    id: "M56789",
    name: "Aisha Binti Hassan",
    phone: "018-765-4321",
    tier: "Gold",
    lastActivity: "2025-02-10",
    daysInactive: 99,
    totalPoints: 6540
  }
];

const MemberInsightReportTab = () => {
  const [insightType, setInsightType] = useState("top-earners");
  const [dateRange, setDateRange] = useState("this-month");
  const [tierFilter, setTierFilter] = useState("all");
  
  const getDataTitle = () => {
    switch (insightType) {
      case "top-earners":
        return "Top Point Earners";
      case "top-redeemers":
        return "Top Point Redeemers";
      case "most-active":
        return "Most Active Members";
      case "inactive":
        return "Inactive Members";
      default:
        return "Member Data";
    }
  };
  
  const exportData = (format: string) => {
    console.log(`Exporting data in ${format} format...`);
    // In a real app, this would trigger download logic
  };
  
  const exportInactiveList = () => {
    console.log("Exporting inactive member list for marketing campaigns...");
    // In a real app, this would export data for marketing tools
  };

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6">Member Insight Report</h2>
      
      {/* Dashboard Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center text-muted-foreground">
              <Users className="h-4 w-4 mr-1" /> Active Members
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData.activeMembers.toLocaleString()}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center text-muted-foreground">
              <TrendingUp className="h-4 w-4 mr-1" /> Points Earned
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData.pointsEarned.toLocaleString()}</div>
            <div className="text-sm text-muted-foreground">This Month</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center text-muted-foreground">
              <TrendingDown className="h-4 w-4 mr-1" /> Points Redeemed
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData.pointsRedeemed.toLocaleString()}</div>
            <div className="text-sm text-muted-foreground">This Month</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center text-muted-foreground">
              <Award className="h-4 w-4 mr-1" /> Redemption Rate
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData.redemptionRate}%</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center text-muted-foreground">
              <Search className="h-4 w-4 mr-1" /> Avg Points / Transaction
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData.avgPointsPerTransaction}</div>
          </CardContent>
        </Card>
      </div>
      
      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Insight Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Select value={insightType} onValueChange={setInsightType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select insight type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="top-earners">Top Earners</SelectItem>
                  <SelectItem value="top-redeemers">Top Redeemers</SelectItem>
                  <SelectItem value="most-active">Most Active Members</SelectItem>
                  <SelectItem value="inactive">Inactive Members</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select date range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="this-week">This Week</SelectItem>
                  <SelectItem value="this-month">This Month</SelectItem>
                  <SelectItem value="last-month">Last Month</SelectItem>
                  <SelectItem value="last-quarter">Last Quarter</SelectItem>
                  <SelectItem value="last-year">Last Year</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Select value={tierFilter} onValueChange={setTierFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Select tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tiers</SelectItem>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="silver">Silver</SelectItem>
                  <SelectItem value="gold">Gold</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Member Insight Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>{getDataTitle()}</CardTitle>
            <CardDescription>
              {insightType === "inactive" ? 
                "Members with no activity in the last 90 days" : 
                `Data for ${dateRange.replace('-', ' ')}`
              }
            </CardDescription>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" size="sm" onClick={() => exportData("excel")}>
              <FileDown className="h-4 w-4 mr-2" /> Excel
            </Button>
            <Button variant="outline" size="sm" onClick={() => exportData("pdf")}>
              <FileDown className="h-4 w-4 mr-2" /> PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {insightType !== "inactive" ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead className="text-right">Total Points</TableHead>
                  <TableHead>Last Activity</TableHead>
                  <TableHead className="text-right">Total Spent (RM)</TableHead>
                  <TableHead className="text-right">Redemptions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sampleMemberData.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>{member.id}</TableCell>
                    <TableCell>{member.name}</TableCell>
                    <TableCell>{member.phone}</TableCell>
                    <TableCell>
                      <Badge variant={
                        member.tier === "Gold" ? "default" :
                        member.tier === "Silver" ? "secondary" :
                        "outline"
                      }>
                        {member.tier}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">{member.totalPoints.toLocaleString()}</TableCell>
                    <TableCell>{member.lastActivity}</TableCell>
                    <TableCell className="text-right">{member.totalSpent.toFixed(2)}</TableCell>
                    <TableCell className="text-right">{member.redemptions}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Last Activity</TableHead>
                  <TableHead className="text-right">Days Inactive</TableHead>
                  <TableHead className="text-right">Points Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sampleInactiveMembersData.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>{member.id}</TableCell>
                    <TableCell>{member.name}</TableCell>
                    <TableCell>{member.phone}</TableCell>
                    <TableCell>
                      <Badge variant={
                        member.tier === "Gold" ? "default" :
                        member.tier === "Silver" ? "secondary" :
                        "outline"
                      }>
                        {member.tier}
                      </Badge>
                    </TableCell>
                    <TableCell>{member.lastActivity}</TableCell>
                    <TableCell className="text-right">{member.daysInactive}</TableCell>
                    <TableCell className="text-right">{member.totalPoints.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
        {insightType === "inactive" && (
          <CardFooter>
            <Button 
              variant="secondary" 
              className="w-full"
              onClick={exportInactiveList}
            >
              <FileDown className="h-4 w-4 mr-2" /> Export Inactive Member List for Marketing
            </Button>
          </CardFooter>
        )}
      </Card>
      
      {/* AI Insights Section */}
      <Card className="mt-6 bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center">
            <span className="mr-2">🧠</span> Smart Insights
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <h3 className="font-medium">Tier Promotion Opportunities</h3>
            <p className="text-sm">
              5 Silver members are within 200 points of reaching Gold status. 
              Consider sending targeted promotions to help them upgrade.
            </p>
          </div>
          
          <div className="space-y-2">
            <h3 className="font-medium">Low Activity Segments</h3>
            <p className="text-sm">
              12 Gold members haven't earned points in over 45 days. 
              Suggest running a bonus points campaign to re-engage these high-value members.
            </p>
          </div>
          
          <div className="space-y-2">
            <h3 className="font-medium">Members Likely to Redeem Soon</h3>
            <p className="text-sm">
              8 members have accumulated over 5,000 points without redemption. 
              Consider sending a reminder about available rewards.
            </p>
            <Button variant="outline" size="sm">
              <Clock className="h-4 w-4 mr-2" /> Schedule Automated Reminder
            </Button>
          </div>
          
          <div className="space-y-2">
            <h3 className="font-medium">Members Close to Tier Upgrade</h3>
            <p className="text-sm">
              15 Basic members are close to Silver tier. 
              Targeted promotion could encourage additional purchases to reach the next tier.
            </p>
            <Button variant="outline" size="sm">
              <Calendar className="h-4 w-4 mr-2" /> Schedule Upgrade Campaign
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MemberInsightReportTab;
